"use client"

import { Avatar } from "@/components/ui/avatar"
import { Camera, Loader2, Linkedin, Globe, ChevronDown, User, ShieldCheck } from "lucide-react"
import { InternalInput } from "@/components/ui/internal-input"
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuRadioGroup, 
    DropdownMenuRadioItem, 
    DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu"
import { Grid, Divider } from "@mui/material"
import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from "react"
import { useUpdateProfileMutation, useUploadProfileImageMutation } from "@/app/store/api/userApi"
import { 
    useGetOnboardingDataQuery, 
    useSaveStep1Mutation, 
    useSaveStep2Mutation, 
    useSaveStep3Mutation 
} from "@/app/store/api/onboardingApi"
import { toast } from "sonner"
import { useAppDispatch } from "@/app/store/hooks"
import { setUser } from "@/app/store/slices/authSlice"
import { cn } from "@/lib/utils"

const ROLES = [
    "Startup Founder",
    "CEO",
    "Product Manager",
    "Working Professional",
    "Aspiring Entrepreneur",
    "Student",
    "Investing Partner",
]

const XIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
    </svg>
)

interface BasicInfoTabProps {
    user: any
    initials: string
    onDeleteAccount?: () => void
}

export interface BasicInfoTabHandle {
    handleSave: () => Promise<boolean>
}

export const BasicInfoTab = forwardRef<BasicInfoTabHandle, BasicInfoTabProps>(
    ({ user, initials, onDeleteAccount }, ref) => {
    const dispatch = useAppDispatch()
    const [firstName, setFirstName] = useState(user?.firstName || "")
    const [lastName, setLastName] = useState(user?.lastName || "")
    const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.imgUrl || null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Onboarding Data State
    const [role, setRoleState] = useState("")
    const [website, setWebsite] = useState("")
    const [linkedin, setLinkedin] = useState("")
    const [twitter, setTwitter] = useState("")

    const [updateProfile] = useUpdateProfileMutation()
    const [uploadProfileImage, { isLoading: isUploadingImage }] = useUploadProfileImageMutation()
    
    // Onboarding API Hooks
    const { data: onboardingData } = useGetOnboardingDataQuery(undefined)
    const [saveStep1] = useSaveStep1Mutation()
    const [saveStep2] = useSaveStep2Mutation()
    const [saveStep3] = useSaveStep3Mutation()

    // Sync state when user data changes
    useEffect(() => {
        if (user) {
            setFirstName(user.firstName || "")
            setLastName(user.lastName || "")
            setAvatarPreview(user.imgUrl || null)
        }
    }, [user])

    // Sync onboarding data
    useEffect(() => {
        if (onboardingData?.success && onboardingData.data) {
            const data = onboardingData.data
            if (data.role) setRoleState(data.role)
            if (data.companyWebsite) setWebsite(data.companyWebsite)
            if (data.socialProfiles) {
                setLinkedin(data.socialProfiles.linkedin || "")
                setTwitter(data.socialProfiles.twitter || "")
            }
        }
    }, [onboardingData])

    const handleImageClick = () => {
        fileInputRef.current?.click()
    }

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setAvatarPreview(reader.result as string)
            }
            reader.readAsDataURL(file)

            try {
                const result = await uploadProfileImage(file).unwrap()
                if (result?.user?.imgUrl) {
                    dispatch(setUser({ ...user, imgUrl: result.user.imgUrl }))
                }
                toast.success("Profile picture updated")
            } catch (error: any) {
                toast.error(error?.data?.message || "Failed to upload profile picture")
                setAvatarPreview(user?.imgUrl || null)
            }
        }
    }

    // Consolidated Save Function
    useImperativeHandle(ref, () => ({
        handleSave: async () => {
            const updates: Promise<any>[] = []
            let hasChanges = false

            // 1. Basic Profile (Name)
            if ((firstName !== user?.firstName || lastName !== user?.lastName) && firstName.trim()) {
                hasChanges = true
                updates.push(updateProfile({ 
                    firstName: firstName.trim(), 
                    lastName: lastName.trim() 
                }).unwrap().then(result => {
                    if (result?.user) dispatch(setUser({ ...user, ...result.user }))
                }))
            }

            // 2. Role (Step 1)
            if (role !== (onboardingData?.data?.role || "")) {
                hasChanges = true
                updates.push(saveStep1({ role }).unwrap())
            }

            // 3. Website (Step 2)
            if (website !== (onboardingData?.data?.companyWebsite || "")) {
                hasChanges = true
                updates.push(saveStep2({ companyWebsite: website }).unwrap())
            }

            // 4. Socials (Step 3)
            const currentLinkedin = onboardingData?.data?.socialProfiles?.linkedin || ""
            const currentTwitter = onboardingData?.data?.socialProfiles?.twitter || ""
            if (linkedin !== currentLinkedin || twitter !== currentTwitter) {
                hasChanges = true
                updates.push(saveStep3({ linkedin, twitter }).unwrap())
            }

            if (!hasChanges) return true

            try {
                await Promise.all(updates)
                toast.success("Profile updated successfully")
                return true
            } catch (error: any) {
                console.error("Save failed:", error)
                toast.error(error?.data?.message || "Failed to save profile changes")
                return false
            }
        }
    }))

    const handleRoleChange = (newRole: string) => {
        setRoleState(newRole)
    }

    return (
        <div className="max-w-[43rem] mx-auto pb-2">
            {/* 1. Identity Header Area */}
            <div className="flex items-start gap-4 mb-5 px-1 pt-2">
                <div className="relative group cursor-pointer flex-shrink-0" onClick={handleImageClick}>
                    <Avatar
                        className="w-20 h-20 text-xl ring-2 ring-slate-100 shadow-sm transition-all group-hover:ring-blue-100"
                        src={avatarPreview || undefined}
                        fallback={initials}
                        fallbackClassName="bg-[#004e92] text-white font-bold"
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-500">
                        {isUploadingImage ? <Loader2 className="w-3.5 animate-spin" /> : <Camera className="w-3.5" />}
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageChange} disabled={isUploadingImage} />
                </div>
                
                <div className="flex-1 space-y-3">
                    <Grid container spacing={1.5}>
                        <Grid size={{ xs: 6 }}>
                            <InternalInput
                                label="First Name"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="h-9 text-xs"
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <InternalInput
                                label="Last Name"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="h-9 text-xs"
                            />
                        </Grid>
                    </Grid>
                    <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-xs text-slate-500 truncate">{user?.email}</span>
                            {user?.emailVerified && <ShieldCheck className="w-3 h-3 text-blue-500 flex-shrink-0" />}
                        </div>
                        {role && (
                            <span className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-[9px] font-bold uppercase tracking-tight">
                                {role}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <Divider className="mb-5 opacity-40" />

            {/* 2. High-Density Sections */}
            <div className="space-y-5 mt-5">
                {/* Professional Identity */}
                <Grid container spacing={2}>
                   
                    <Grid size={{ xs: 6 }}>
                        <div className="space-y-1">
                            <label className="text-[11px] font-semibold text-slate-500 px-1">Your Role</label>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild bgColor="bg-slate-100 hover:bg-slate-200/50">
                                    <button className="flex h-9 w-full items-center justify-between rounded-xl px-3 py-1 text-xs transition-all outline-none border-0 ring-offset-background focus:ring-1 focus:ring-blue-500/20">
                                        <span className={role ? "text-slate-900 font-medium" : "text-slate-400"}>
                                            {role || "Select Role"}
                                        </span>
                                        <ChevronDown className="h-3 w-3 text-slate-400" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-[180px]" align="start">
                                    <DropdownMenuRadioGroup value={role} onValueChange={handleRoleChange}>
                                        {ROLES.map((r) => (
                                            <DropdownMenuRadioItem key={r} value={r} className="text-xs py-1">
                                                {r}
                                            </DropdownMenuRadioItem>
                                        ))}
                                    </DropdownMenuRadioGroup>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <InternalInput
                            label="Company Website"
                            placeholder="website.com"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            startIcon={<Globe className="w-3 h-3 text-slate-400" />}
                            className="h-9 text-xs"
                        />
                    </Grid>
                </Grid>

                {/* Social Presence */}
                <Grid container spacing={2}>
                    
                    <Grid size={{ xs: 6 }}>
                        <InternalInput
                            label="LinkedIn"
                            placeholder="linkedin.com/in/..."
                            value={linkedin}
                            onChange={(e) => setLinkedin(e.target.value)}
                            startIcon={<Linkedin className="w-3 h-3 text-[#0077b5]" />}
                            className="h-9 text-xs"
                        />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <InternalInput
                            label="Twitter (X)"
                            placeholder="twitter.com/..."
                            value={twitter}
                            onChange={(e) => setTwitter(e.target.value)}
                            startIcon={<XIcon className="w-3 h-3 text-slate-900" />}
                            className="h-9 text-xs"
                        />
                    </Grid>
                </Grid>
            </div>

            {/* Account Utility */}
            {onDeleteAccount && (
                <div className="mt-8 pt-4 border-t border-slate-100 flex items-center justify-between px-1">
                    <span className="text-[9px] text-slate-400 flex items-center gap-1">
                        <User className="w-2.5 h-2.5" /> Identity verified
                    </span>
                    <button
                        onClick={onDeleteAccount}
                        className="text-[10px] font-bold text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                    >
                        Delete My Account
                    </button>
                </div>
            )}
        </div>
    )
})

BasicInfoTab.displayName = "BasicInfoTab"
